import React from "react";

import {
    enhanceBodyImagePermalinks,
    MEDIA_PERMALINK_ANCHOR_ATTRIBUTE,
} from "@/main/react/discussion/bodyImagePermalinks";
import { enhanceBodyClipPlayers } from "@/main/react/media/bodyClipPlayers";
import type { MediaPermalinkHint } from "@/main/util/MediaPermalink";
import { useBackDismissesOverlay } from "@/main/navigation/useBackDismissesOverlay";
import MediaLightboxLazy from "@/main/react/media/MediaLightboxLazy";
import type { MediaLightboxItem } from "@/main/react/media/MediaLightbox";
import type { Localizer } from "@/main/react/Types";
import { isPageInterceptableClick } from "@/main/navigation/NavigationPolicy";
import { recordMediaOpen } from "@/main/react/discussion/MediaAttentionSession";

/**
 * Opens the full-screen viewer when a reader clicks an image inside a discussion or thought body.
 *
 * <p>Supersedes `useBodyImagePermalinks` at its call sites. It does not replace that module — it CALLS
 * it, unchanged — so the `<a href="/media/{id}">` wrapper every body image already gets is still the
 * substrate, and this hook only decides what a plain left-click on one means.
 *
 * <p><b>Additive by construction (AGENTS.md rule 32).</b> The anchor, its `href` and the `<img>` inside
 * it are never touched. Only a plain, unmodified primary click is intercepted; a modified click, a
 * middle click, a click something else already handled, and every no-JS/crawler path fall through to the
 * anchor exactly as before. If the overlay chunk never loads, the anchor is still a working link — the
 * worst case is today's product, never less than it.
 *
 * <p><b>Back closes the overlay (rule 33).</b> Opening writes ONE sacrificial history entry through
 * `pushGuardHistoryEntry` — stamped by `NavigationHistoryState`, never a hand-built or spread entry —
 * and registers a `PopStateGuard`, which the router consults before it acts on any pop. The address bar
 * is deliberately left alone: the viewer is a view state of the page it opened from, not a route.
 *
 * Design: designs/01-now/2026-08-01-media-lightbox-full-screen-viewer-design.md §3.2, §3.3
 */


/**
 * The marker stamped onto the lightbox's sacrificial entry and read back before it is popped. One
 * constant so the write and the ownership check cannot drift — a close that reads a marker the open
 * never wrote refuses every pop, silently, and looks exactly like a lightbox that simply never armed.
 */
const MEDIA_LIGHTBOX_GUARD_MARKER = "seavienMediaLightbox";

export type UseBodyMediaLightboxOptions = {
    /** Where the body lives, for the `?in=` attribution hint on the permalinks. */
    hint?: MediaPermalinkHint | null;
    /** The rendered HTML, so enhancement and interception re-run when the body changes. */
    contentHtml?: string | null;
    localizer: Localizer;
};

export type UseBodyMediaLightboxResult<T extends HTMLElement> = {
    /**
     * Attach to the element hosting `dangerouslySetInnerHTML` — the body container ONLY, never a
     * wrapper that also holds chrome. An author avatar caught by this would start opening the viewer
     * instead of the profile (the same constraint `bodyImagePermalinks` documents for its wrapping).
     */
    ref: React.RefObject<T | null>;
    /** Render anywhere inside the host; `null` until a body image is clicked. */
    lightbox: React.ReactNode;
};

const PERMALINK_ANCHOR_SELECTOR = `a[${MEDIA_PERMALINK_ANCHOR_ATTRIBUTE}="true"]`;

type OpenState = {
    items: readonly MediaLightboxItem[];
    index: number;
};

/**
 * Every permalink-wrapped image in the body, in DOM order, paired with the anchor that owns it — so a
 * click resolves to an index in the same list the viewer will page through.
 */
function collectBodyMedia(root: HTMLElement): { items: MediaLightboxItem[]; anchors: HTMLAnchorElement[] } {
    const items: MediaLightboxItem[] = [];
    const anchors: HTMLAnchorElement[] = [];

    root.querySelectorAll<HTMLAnchorElement>(PERMALINK_ANCHOR_SELECTOR).forEach((anchor) => {
        const image = anchor.querySelector("img[src]");
        const src = image?.getAttribute("src")?.trim();
        if (!src) {
            return;
        }
        items.push({
            src,
            alt: image?.getAttribute("alt") ?? "",
            permalinkHref: anchor.getAttribute("href"),
        });
        anchors.push(anchor);
    });

    return { items, anchors };
}

export function useBodyMediaLightbox<T extends HTMLElement = HTMLDivElement>(
    { hint, contentHtml, localizer }: UseBodyMediaLightboxOptions,
): UseBodyMediaLightboxResult<T> {
    const ref = React.useRef<T | null>(null);
    const [openState, setOpenState] = React.useState<OpenState | null>(null);

    const kind = hint?.kind;
    const contentId = hint?.contentId;

    /**
     * The existing permalink wrapping, unchanged — this hook CALLS `bodyImagePermalinks`, it does not
     * fork it.
     *
     * <p><b>Deliberately no dependency array.</b> React re-applies `dangerouslySetInnerHTML` when the
     * host re-renders with a fresh `{__html}` object, which DESTROYS every anchor this wrapped — the
     * `<img>` nodes come back bare. `useBodyImagePermalinks` re-ran only when the HTML changed, so a
     * host that re-rendered for any other reason silently lost its body-image links for the rest of the
     * page's life, and nothing anywhere would have reported it.
     *
     * <p>That was latent before this hook and is not any more: opening the viewer IS a host re-render,
     * so a deps-gated effect would have made the first click the thing that broke every later one.
     * `DiscussionContentDisplayer` happens to be immune because it memoizes the article element (React
     * bails out on a reference-identical element), but relying on a `useMemo` in one call site to keep
     * a DOM invariant in another is not a contract — re-running is.
     *
     * <p>Cheap by construction: `enhanceBodyImagePermalinks` is idempotent, skips any image already
     * inside an anchor, and does one `querySelectorAll` over a body.
     */
    React.useEffect(() => {
        // `DISCUSSION-7` — the third argument is the placeholder's readable label. It is passed from
        // here rather than resolved inside `bodyImagePermalinks` because that module enhances injected
        // DOM and has no React context; `enhanceBodyClipPlayers` below takes a localizer for the same
        // reason. The key has shipped in all 25 catalogs since wave 6.
        enhanceBodyImagePermalinks(
            ref.current,
            kind && contentId ? { kind, contentId } : null,
            localizer("discussion.body.image_unavailable"),
        );
        // Same effect, same reasoning, same idempotency contract — and deliberately AFTER the permalink
        // pass, which skips clip posters so the two never contend for the same `<img>`. Running it here
        // rather than in its own hook is what makes the clip player appear on exactly the two surfaces
        // that enhance body HTML at all (discussion body, thought-in-discussion); the four that inject
        // raw HTML keep rendering the poster as a still image, which is correct and costs nothing.
        // The `d` hint's id rides along for the clip player's play / watch-through signals (`2c`), on
        // the same attribution rule `openAt` applies to a lightbox open: a thought's clip on a
        // discussion page is not the post's.
        enhanceBodyClipPlayers(ref.current, localizer, kind === "d" ? contentId : null);
    });

    const closeOverlay = React.useCallback(() => {
        setOpenState(null);
    }, []);

    /**
     * The whole overlay-Back lifecycle, DELEGATED (FA-11, 2026-09-20): the stamped sacrificial entry on
     * the closed→open edge, the pop guard the router consults, the self-pop bookkeeping, the
     * soft-navigation departure observer and the unmount-time pop of an orphaned entry.
     *
     * <p>This file is where `useBackDismissesOverlay` was extracted FROM, and it kept its own copy
     * afterwards. The hook then gained two behaviours the copy never got — the departure observer
     * (2026-08-27, the prod report "the page changes BUT the sidebar doesn't actually close") and the
     * unmount cleanup — so by 2026-09-20 the hook's doc comment calling this the correct implementation
     * had been false for three weeks. A copy of a lifecycle is a copy of its bug history frozen at the
     * moment of copying; there is one implementation now.
     *
     * <p>`popOwnGuardEntry`'s §13.5 ownership check is inherited, not lost: it lives centrally in
     * `NavigationHistoryState`, which the hook calls.
     */
    useBackDismissesOverlay({
        open: openState !== null,
        onRequestClose: closeOverlay,
        marker: MEDIA_LIGHTBOX_GUARD_MARKER,
    });

    const openAt = React.useCallback((items: readonly MediaLightboxItem[], index: number) => {
        setOpenState({ items, index });
        // P3 `opens` (media-attention design §4; slice `MD-2b`): an open is an intent. Credited to the
        // page instance's session for THIS discussion only, so a thought's body image opened on a
        // discussion page (a `t` hint) is not counted against the post. Nothing is posted from here:
        // the tracker that owns the session sends once, at hide/unload. Keyed on the hint's fields
        // rather than the object so a re-created hint does not re-bind the body's click listener.
        recordMediaOpen(kind === "d" ? contentId : null);
    }, [kind, contentId]);

    React.useEffect(() => {
        const root = ref.current;
        if (!root) {
            return;
        }

        const onClick = (event: MouseEvent) => {
            // The rule-32 fall-through set: anything that is not a plain primary click keeps today's
            // behaviour, which is the anchor's own navigation — open-in-new-tab / new-window /
            // download must still reach it. Shared predicate since 2026-09-07 (`FA-R2`).
            if (!isPageInterceptableClick(event)) {
                return;
            }

            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const anchor = target.closest<HTMLAnchorElement>(PERMALINK_ANCHOR_SELECTOR);
            if (!anchor || !root.contains(anchor)) {
                return;
            }

            const { items, anchors } = collectBodyMedia(root);
            const index = anchors.indexOf(anchor);
            if (index < 0 || items.length === 0) {
                return; // nothing to show — leave the navigation alone rather than swallowing the click
            }

            event.preventDefault();
            openAt(items, index);
        };

        root.addEventListener("click", onClick);
        return () => {
            root.removeEventListener("click", onClick);
        };
    }, [contentHtml, openAt]);

    const lightbox = openState
        ? (
            <MediaLightboxLazy
                items={openState.items}
                initialIndex={openState.index}
                localizer={localizer}
                onClose={closeOverlay}
            />
        )
        : null;

    return { ref, lightbox };
}
